import React, { useState, useEffect, useMemo } from 'react';
import { useOrderStore } from '../../store/orderStore';
import ProductSelector from '../ProductSelector';
import { ProductOptionsRenderer } from '../ProductOptionsRenderer';
import { SizeSelector } from '../OptionSelectors';
import { Button } from '../ui/button';
import { RealProduct } from '../../services/realShopifyAPI';
import { MockProduct } from '../../services/devShopifyAPI';
import { ChevronRight } from 'lucide-react';
import printOptions from '../../data/options/Print.json';
import overmatOptions from '../../data/options/Overmat.json';
import panelOptions from '../../data/options/Panel.json';

type Product = RealProduct | MockProduct;
type OptionValues = { [key: string]: string | number | boolean };

// Helper to extract all field names from option definition
const getOptionKeys = (options: any[]): string[] => {
    const keys: string[] = [];
    if (!options || !options.length) return keys;

    const definition = options[0];
    Object.values(definition.fields).forEach((section: any) => {
        section.fields.forEach((row: any[]) => {
            row.forEach((field: any) => {
                keys.push(field.name);
            });
        });
    });
    return keys;
};

export const LeftSidebar: React.FC = () => {
    const {
        selectedCategory,
        selectedProduct,
        setSelectedProduct,
        selectedFrameProduct,
        setSelectedFrameProduct,
        selectedSize,
        setSize,
        productOptions,
        setProductOptions
    } = useOrderStore();

    const [showPaperSelector, setShowPaperSelector] = useState(false);
    const [showFrameSelector, setShowFrameSelector] = useState(false);
    const [showMatProcessingSelector, setShowMatProcessingSelector] = useState(false);
    const [showPanelSelector, setShowPanelSelector] = useState(false);
    const [showCanvasSelector, setShowCanvasSelector] = useState(false);

    // Calculate keys for each option set
    const printKeys = useMemo(() => getOptionKeys(printOptions), []);
    const panelKeys = useMemo(() => getOptionKeys(panelOptions), []);
    const overmatKeys = useMemo(() => getOptionKeys(overmatOptions), []);

    // Reset selected frame product when category changes
    useEffect(() => {
        setSelectedFrameProduct(null);
    }, [selectedCategory, setSelectedFrameProduct]);

    // Handle category changes to reset irrelevant options
    useEffect(() => {
        let keysToRemove: string[] = [];

        if (selectedCategory === 'print') {
            // Remove panel and overmat options, but keep those that are also in print options (shared IDs)
            keysToRemove = [...panelKeys, ...overmatKeys].filter(key => !printKeys.includes(key));
        } else if (selectedCategory === 'frame') {
            // Frame allows everything, but maybe we should be specific?
            // For now, frame includes print options, so we don't remove printKeys.
            // If frame doesn't support some panel/overmat specific options that are NOT shared, we might need to remove them.
            // But based on requirements:
            // Frame -> Show Panel options? Yes (if selectedCategory is frame, showPanelOptions is true)
            // Wait, showPanelOptions logic: selectedCategory === 'frame' || selectedCategory === 'panel'

            // Actually, let's look at the visibility logic:
            // showPrintOptions = true (always)
            // showPanelOptions = frame || panel
            // showOvermatOptions = frame || mat-processing

            // So if category is 'frame', we don't remove anything from panel/overmat/print.
            keysToRemove = [];
        } else if (selectedCategory === 'panel') {
            // Show Print + Panel. Hide Overmat (unless shared).
            keysToRemove = overmatKeys.filter(key => !printKeys.includes(key) && !panelKeys.includes(key));
        } else if (selectedCategory === 'mat-processing') {
            // Show Print + Overmat. Hide Panel (unless shared).
            keysToRemove = panelKeys.filter(key => !printKeys.includes(key) && !overmatKeys.includes(key));
        } else if (selectedCategory === 'canvas') {
            // Canvas might have its own options, but for now treat like print?
            // Assuming canvas only uses print options or has its own file not yet handled.
            // If canvas uses print options, we should remove panel/overmat.
            keysToRemove = [...panelKeys, ...overmatKeys].filter(key => !printKeys.includes(key));
        }

        if (keysToRemove.length > 0) {
            // Create an update object setting removed keys to undefined (or empty string if preferred, but undefined removes them from merge usually, or we need to check store implementation)
            // If store merge uses { ...state, ...update }, undefined will overwrite.
            // However, if we want to truly remove, we might need a different approach.
            // For now, setting to undefined or null should be enough for the renderer to see "empty".
            const update = keysToRemove.reduce((acc, key) => {
                // Only reset if it currently has a value
                if (productOptions[key] !== undefined) {
                    acc[key] = undefined as any;
                }
                return acc;
            }, {} as OptionValues);

            if (Object.keys(update).length > 0) {
                console.log('Resetting options:', update);
                setProductOptions(update);
            }
        }
    }, [selectedCategory, panelKeys, overmatKeys, printKeys, setProductOptions, productOptions]);

    const handleOptionValuesChange = React.useCallback((values: OptionValues) => {
        console.log('LeftSidebar handleOptionValuesChange:', values);
        setProductOptions(values);
    }, [setProductOptions]);

    const handlePaperSelect = (product: Product, variantId?: string) => {
        setSelectedProduct(product);
        // Reset or set size when paper changes
        if (variantId) {
            setSize(variantId);
        } else {
            setSize('');
        }
    };

    const handleFrameSelect = (product: Product) => {
        setSelectedFrameProduct(product);
    };

    const filteredPanelOptions = useMemo(() => {
        return (panelOptions as any).map((section: any) => ({
            ...section,
            fields: Object.entries(section.fields).reduce((acc, [key, sec]: [string, any]) => {
                const filteredFields = sec.fields.map((row: any[]) =>
                    row.filter((field: any) =>
                        ![
                            'select_2242572071', // Paper Selection
                            'select_0304312790', // Print Layout
                            'number_4064523402', // Image Size Short
                            'number-5213823459', // Image Size Long
                            'product_group_2131024012', // Options/Test Print
                            'checkgroup_1096061030', // Layout Confirmation
                            'textarea-2117761297', // Remarks
                            'file_5425531660', // File Upload
                            'content_7756311678', // Upload count
                            'group_validation_7829141679', // Group Validation
                            'textarea_1850796981' // Data Remarks
                        ].includes(field.elementId || field.name)
                    )
                ).filter((row: any[]) => row.length > 0);

                if (filteredFields.length > 0) {
                    acc[key] = { ...sec, fields: filteredFields };
                }
                return acc;
            }, {} as any)
        }));
    }, []);

    const filteredOvermatOptions = useMemo(() => {
        return (overmatOptions as any).map((section: any) => ({
            ...section,
            fields: Object.entries(section.fields).reduce((acc, [key, sec]: [string, any]) => {
                const filteredFields = sec.fields.map((row: any[]) =>
                    row.filter((field: any) =>
                        ![
                            'product_group_5883503500', // Options/Test Print
                            'checkgroup_1096061030', // Layout Confirmation
                            'textarea-2117761297', // Remarks
                            'file_5425531660', // File Upload
                            'content_7756311678', // Upload count
                            'group_validation_7829141679', // Group Validation
                            'textarea_1850796981' // Data Remarks
                        ].includes(field.elementId || field.name)
                    )
                ).filter((row: any[]) => row.length > 0);

                if (filteredFields.length > 0) {
                    acc[key] = { ...sec, fields: filteredFields };
                }
                return acc;
            }, {} as any)
        }));
    }, []);

    // Determine visibility
    const showPrintOptions = true; // Always show Print options
    const showPanelOptions = selectedCategory === 'frame' || selectedCategory === 'panel';
    const showOvermatOptions = (selectedCategory === 'frame' || selectedCategory === 'mat-processing') && (
        !selectedFrameProduct ||
        selectedFrameProduct.title.includes('マット')
    );

    const filteredPrintOptions = useMemo(() => {
        return (printOptions as any).map((section: any) => ({
            ...section,
            fields: Object.entries(section.fields).reduce((acc, [key, sec]: [string, any]) => {
                const filteredFields = sec.fields.map((row: any[]) =>
                    row.filter((field: any) =>
                        ![
                            'image_6763090628',
                            'number-4469530033',
                            'number-4469794842',
                            'content_0743538113',
                            'content_9233031758',
                            'content_6562650546',
                            'file_5425531660',          // File upload
                            'content_7756311678',       // Upload count
                            'group_validation_7829141679', // Group Validation
                            'textarea_1850796981'       // Data Remarks
                        ].includes(field.elementId || field.name)
                    )
                ).filter((row: any[]) => row.length > 0);

                if (filteredFields.length > 0) {
                    acc[key] = { ...sec, fields: filteredFields };
                }
                return acc;
            }, {} as any)
        }));
    }, []);

    return (
        <div className="w-full lg:w-80 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 h-full overflow-y-auto flex flex-col">
            <div className="p-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-800">
                    {selectedCategory === 'print' && '印刷設定'}
                    {selectedCategory === 'frame' && '額装設定'}
                    {selectedCategory === 'mat-processing' && 'マット加工設定'}
                    {selectedCategory === 'panel' && 'パネル加工設定'}
                    {selectedCategory === 'canvas' && 'キャンバス設定'}
                </h2>
            </div>

            <div className="p-4 space-y-6 flex-1">
                {/* 用紙選択セクション */}
                <section>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wider">用紙選択</h3>
                    {selectedProduct ? (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex justify-between items-start mb-2">
                                <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">選択中</span>
                                <button
                                    onClick={() => setShowPaperSelector(true)}
                                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                                >
                                    変更
                                </button>
                            </div>
                            <p className="font-medium text-gray-900 text-sm mb-1">{selectedProduct.title}</p>
                            <p className="text-xs text-gray-600">
                                ¥{selectedProduct.variants?.[0]?.price || 'N/A'}〜
                            </p>
                        </div>
                    ) : (
                        <Button
                            onClick={() => setShowPaperSelector(true)}
                            className="w-full justify-between"
                            variant="outline"
                        >
                            <span>用紙を選択</span>
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    )}
                </section>

                {/* サイズ選択セクション */}
                <section>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wider">サイズ選択</h3>
                    {selectedProduct ? (
                        <div className="space-y-3">
                            <SizeSelector
                                optionIds={{
                                    deliverySize: 'image_6763090628',
                                    width: 'number-4469530033',
                                    height: 'number-4469794842'
                                }}
                            />
                        </div>
                    ) : (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-500 text-sm">
                            用紙を選択すると<br />サイズが表示されます
                        </div>
                    )}
                </section>

                {/* 額装&加工選択セクション (額装のみ) */}
                {selectedCategory === 'frame' && (
                    <section>
                        <h3 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wider">額装&加工選択</h3>
                        {selectedFrameProduct ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">選択中</span>
                                    <button
                                        onClick={() => setShowFrameSelector(true)}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                                    >
                                        変更
                                    </button>
                                </div>
                                <p className="font-medium text-gray-900 text-sm mb-1">{selectedFrameProduct.title}</p>
                                <p className="text-xs text-gray-600">
                                    ¥{selectedFrameProduct.variants?.[0]?.price || 'N/A'}〜
                                </p>
                            </div>
                        ) : (
                            <Button
                                onClick={() => setShowFrameSelector(true)}
                                className="w-full justify-between mb-4"
                                variant="outline"
                            >
                                <span>額装&加工を選択</span>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        )}
                    </section>
                )}

                {/* マット加工選択セクション */}
                {selectedCategory === 'mat-processing' && (
                    <section>
                        <h3 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wider">マット加工選択</h3>
                        {selectedFrameProduct ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">選択中</span>
                                    <button
                                        onClick={() => setShowMatProcessingSelector(true)}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                                    >
                                        変更
                                    </button>
                                </div>
                                <p className="font-medium text-gray-900 text-sm mb-1">{selectedFrameProduct.title}</p>
                                <p className="text-xs text-gray-600">
                                    ¥{selectedFrameProduct.variants?.[0]?.price || 'N/A'}〜
                                </p>
                            </div>
                        ) : (
                            <Button
                                onClick={() => setShowMatProcessingSelector(true)}
                                className="w-full justify-between"
                            >
                                <span>マット加工を選択</span>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        )}
                    </section>
                )}

                {/* パネル選択セクション */}
                {selectedCategory === 'panel' && (
                    <section>
                        <h3 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wider">パネル選択</h3>
                        {selectedFrameProduct ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">選択中</span>
                                    <button
                                        onClick={() => setShowPanelSelector(true)}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                                    >
                                        変更
                                    </button>
                                </div>
                                <p className="font-medium text-gray-900 text-sm mb-1">{selectedFrameProduct.title}</p>
                                <p className="text-xs text-gray-600">
                                    ¥{selectedFrameProduct.variants?.[0]?.price || 'N/A'}〜
                                </p>
                            </div>
                        ) : (
                            <Button
                                onClick={() => setShowPanelSelector(true)}
                                className="w-full justify-between"
                            >
                                <span>パネルを選択</span>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        )}
                    </section>
                )}

                {/* キャンバス選択セクション */}
                {selectedCategory === 'canvas' && (
                    <section>
                        <h3 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wider">キャンバス選択</h3>
                        {selectedFrameProduct ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">選択中</span>
                                    <button
                                        onClick={() => setShowCanvasSelector(true)}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                                    >
                                        変更
                                    </button>
                                </div>
                                <p className="font-medium text-gray-900 text-sm mb-1">{selectedFrameProduct.title}</p>
                                <p className="text-xs text-gray-600">
                                    ¥{selectedFrameProduct.variants?.[0]?.price || 'N/A'}〜
                                </p>
                            </div>
                        ) : (
                            <Button
                                onClick={() => setShowCanvasSelector(true)}
                                className="w-full justify-between"
                            >
                                <span>キャンバスを選択</span>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        )}
                    </section>
                )}

                {/* オプション設定セクション */}
                {selectedProduct && (
                    <section className="mt-6">
                        <h3 className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wider">設定オプション</h3>
                        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-6">
                            {/* Print Options (Always Visible) */}
                            {showPrintOptions && (
                                <ProductOptionsRenderer
                                    values={productOptions}
                                    onValuesChange={handleOptionValuesChange}
                                    selectedProduct={selectedProduct}
                                    optionData={filteredPrintOptions}
                                />
                            )}

                            {/* Panel Options */}
                            {showPanelOptions && (
                                <div className="border-t border-gray-200 pt-6">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-4">パネル加工オプション</h4>
                                    <ProductOptionsRenderer
                                        optionData={filteredPanelOptions}
                                        values={productOptions}
                                        onValuesChange={handleOptionValuesChange}
                                        selectedProduct={selectedProduct}
                                        additionalContext={{ product_skus: 'customwoodpanel' }}
                                    />
                                </div>
                            )}

                            {/* Overmat Options */}
                            {showOvermatOptions && (
                                <div className="border-t border-gray-200 pt-6">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-4">マット加工オプション</h4>
                                    <ProductOptionsRenderer
                                        optionData={filteredOvermatOptions}
                                        values={productOptions}
                                        onValuesChange={handleOptionValuesChange}
                                        selectedProduct={selectedProduct}
                                        additionalContext={{ product_skus: 'customalminiumovermat' }}
                                    />
                                </div>
                            )}
                        </div>
                    </section>
                )}
            </div>

            {/* モーダル */}
            {showPaperSelector && (
                <ProductSelector
                    onProductSelect={handlePaperSelect}
                    onClose={() => setShowPaperSelector(false)}
                    filterType="paper"
                />
            )}

            {showFrameSelector && (
                <ProductSelector
                    onProductSelect={handleFrameSelect}
                    onClose={() => setShowFrameSelector(false)}
                    filterType="frame"
                />
            )}

            {showMatProcessingSelector && (
                <ProductSelector
                    onProductSelect={handleFrameSelect}
                    onClose={() => setShowMatProcessingSelector(false)}
                    filterType="mat-processing"
                />
            )}

            {showPanelSelector && (
                <ProductSelector
                    onProductSelect={handleFrameSelect}
                    onClose={() => setShowPanelSelector(false)}
                    filterType="panel"
                />
            )}

            {showCanvasSelector && (
                <ProductSelector
                    onProductSelect={handleFrameSelect}
                    onClose={() => setShowCanvasSelector(false)}
                    filterType="canvas"
                />
            )}
        </div>
    );
};
